import React, { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import {
  CLASSIFICATION_OPTIONS,
  ClassificationValue,
  getClassificationGroup,
  getClassificationLabel,
} from "@utils/classification";

type Props = {
  label?: string;
  value?: ClassificationValue;
  onChange: (value: ClassificationValue) => void;
  error?: string;
  placeholder?: string;
};

const ClassificationSelector: React.FC<Props> = ({
  label = "Classification",
  value,
  onChange,
  error,
  placeholder = "Select classification",
}) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [showSubOptions, setShowSubOptions] = useState(false);

  const selectedGroup = useMemo(
    () => getClassificationGroup(value?.main),
    [value?.main]
  );

  const selectedSubValues = value?.sub || [];

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setShowSubOptions(Boolean(value?.main));
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [value?.main]);

  useEffect(() => {
    if (isOpen) {
      setShowSubOptions(Boolean(selectedGroup));
    }
  }, [isOpen, selectedGroup]);

  const displayText = useMemo(() => {
    if (!value?.main) return placeholder;

    const mainLabel = getClassificationLabel(value.main);
    if (!selectedSubValues.length) return mainLabel;

    const selectedSubCount = selectedSubValues.length;
    if (selectedSubCount === 1) {
      const selectedSub =
        selectedGroup?.subOptions?.find(
          (option) =>
            option.value === selectedSubValues[0].toLowerCase() ||
            option.label.toLowerCase() === selectedSubValues[0].toLowerCase()
        )?.label || selectedSubValues[0];
      return `${mainLabel} - ${selectedSub}`;
    }

    return `${mainLabel} (${selectedSubCount} selected)`;
  }, [placeholder, selectedGroup, selectedSubValues, value?.main]);

  const handleMainSelect = (mainValue: string) => {
    onChange({ main: mainValue, sub: [] });
    setShowSubOptions(true);
  };

  const handleSubToggle = (subValue: string) => {
    if (!selectedGroup?.value) return;

    const normalized = subValue.toLowerCase();
    const nextValues = selectedSubValues.some(
      (item) => item.toLowerCase() === normalized
    )
      ? selectedSubValues.filter((item) => item.toLowerCase() !== normalized)
      : [...selectedSubValues, subValue];

    onChange({
      main: selectedGroup.value,
      sub: nextValues,
    });
  };

  return (
    <div ref={wrapperRef} className="w-full">
      {label && (
        <p className="text-primary text-xs font-medium mb-2">
          {label} <span className="text-red-500">*</span>
        </p>
      )}

      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen((prev) => !prev)}
          className="w-full rounded-md border px-3 py-2 bg-white text-left flex items-center justify-between hover:border-gray-400"
        >
          <span
            className={`text-xs font-medium ${
              value?.main ? "text-primary" : "text-[#C3C3C3]"
            }`}
          >
            {displayText}
          </span>
          <ChevronDown
            className={`h-4 w-4 text-gray-400 transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </button>

        {isOpen && (
          <div className="absolute z-20 mt-1 w-full rounded-md border bg-white shadow-lg max-h-80 overflow-auto">
            {!showSubOptions ? (
              <>
                {CLASSIFICATION_OPTIONS.map((group) => {
                  const isSelected =
                    selectedGroup?.value === group.value ||
                    value?.main?.toLowerCase() === group.value;
                  return (
                    <button
                      key={group.value}
                      type="button"
                      onClick={() => handleMainSelect(group.value)}
                      className={`w-full px-4 py-2 text-left flex items-center gap-3 hover:bg-gray-50 ${
                        isSelected ? "bg-gray-50" : ""
                      }`}
                    >
                      <span
                        className={`flex h-4 w-4 items-center justify-center rounded border ${
                          isSelected
                            ? "border-gray-700 bg-gray-700"
                            : "border-gray-300"
                        }`}
                      >
                        {isSelected ? (
                          <Check className="h-3 w-3 text-white" />
                        ) : null}
                      </span>
                      <span className="text-xs font-medium text-primary">
                        {group.label}
                      </span>
                    </button>
                  );
                })}
              </>
            ) : (
              <>
                <div className="border-b px-4 py-2 text-xs font-semibold text-gray-600 bg-gray-50">
                  {selectedGroup?.label || "Sub classifications"}
                </div>

                {selectedGroup?.subOptions?.map((subOption) => {
                  const isSelected = selectedSubValues.some(
                    (item) => item.toLowerCase() === subOption.value.toLowerCase()
                  );
                  return (
                    <button
                      key={subOption.value}
                      type="button"
                      onClick={() => handleSubToggle(subOption.value)}
                      className={`w-full px-4 py-2 text-left flex items-center gap-3 hover:bg-gray-50 ${
                        isSelected ? "bg-gray-50" : ""
                      }`}
                    >
                      <span
                        className={`flex h-4 w-4 items-center justify-center rounded border ${
                          isSelected
                            ? "border-gray-700 bg-gray-700"
                            : "border-gray-300"
                        }`}
                      >
                        {isSelected ? (
                          <Check className="h-3 w-3 text-white" />
                        ) : null}
                      </span>
                      <span className="text-xs font-medium text-primary">
                        {subOption.label}
                      </span>
                    </button>
                  );
                })}

                <div className="border-t px-4 py-2">
                  <button
                    type="button"
                    onClick={() => setShowSubOptions(false)}
                    className="text-xs font-medium text-gray-600 hover:text-gray-800"
                  >
                    Back to main categories
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {!!selectedSubValues.length && (
        <div className="mt-2 flex flex-wrap gap-2">
          {selectedSubValues.map((subValue) => {
            const selectedLabel =
              selectedGroup?.subOptions?.find(
                (option) =>
                  option.value === subValue.toLowerCase() ||
                  option.label.toLowerCase() === subValue.toLowerCase()
              )?.label || subValue;

            return (
              <span
                key={subValue}
                className="inline-flex items-center rounded-full bg-blue-100 px-2 py-1 text-[11px] font-medium text-gray-800"
              >
                {selectedLabel}
                <button
                  type="button"
                  onClick={() => handleSubToggle(subValue)}
                  className="ml-1 text-sm leading-none"
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      )}

      {error ? (
        <span className="mt-2 block text-xs font-medium text-orange-700">
          {error}
        </span>
      ) : null}
    </div>
  );
};

export default ClassificationSelector;

